/* eslint-disable react-refresh/only-export-components */

import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { fetchCollaborationMap } from '@/api/supply-chain-mapping';
import { useCollaborationMap } from '@/app/(client)/supply-chain-mapping/-hooks/useCollaborationMap';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { SupplyChainGeoMap } from '@/components/shared/SupplyChainGeoMap';
import type {
  SupplyChainGeoMapEdge,
  SupplyChainGeoMapNode,
} from '@/components/shared/SupplyChainGeoMap/types';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/supply-chain-mapping/(pages)/(details)/supply-chains/$id/map/',
)({
  loader: ({ params }) =>
    queryClient
      .ensureQueryData({
        queryKey: ['supplyChainMapping', 'collaborationMap', params.id],
        queryFn: () => fetchCollaborationMap(params.id),
        staleTime: 60_000,
      })
      .catch(() => undefined),
  component: SupplyChainMapPage,
});

function SupplyChainMapPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { getTranslation: t } = useTranslator({ page: 'supplyChainMapping' });
  const { data, isLoading, error, refetch } = useCollaborationMap(id);

  const { nodes, edges } = useMemo(() => {
    const companies = data?.companies ?? [];
    const valid = companies.filter(
      c =>
        Array.isArray(c.position) &&
        Number.isFinite(c.position[0]) &&
        Number.isFinite(c.position[1]),
    );
    const builtNodes: SupplyChainGeoMapNode[] = valid.map(c => ({
      id: c.id,
      name: c.officialName,
      level: c.level,
      position: c.position,
    }));
    const validIds = new Set(valid.map(c => c.id));
    const builtEdges: SupplyChainGeoMapEdge[] = [];
    for (const c of valid) {
      for (const targetId of c.connectedTo ?? []) {
        if (validIds.has(targetId)) {
          builtEdges.push({ fromId: c.id, toId: targetId });
        }
      }
    }
    return { nodes: builtNodes, edges: builtEdges };
  }, [data]);

  return (
    <div className='flex-1 min-h-0 flex flex-col px-6 pt-4 pb-6'>
      <Card className='flex-1 min-h-0 overflow-hidden p-0 gap-0'>
        <CardContent className='flex-1 min-h-0 p-0'>
          {error ? (
            <QueryErrorState
              error={error}
              onRetry={() => refetch()}
              className='flex-1 h-full'
            />
          ) : !isLoading && nodes.length === 0 ? (
            <EmptyState
              title={t('detail.map.empty')}
              className='flex-1 h-full'
            />
          ) : (
            <SupplyChainGeoMap
              nodes={nodes}
              edges={edges}
              labels={{
                facility: t('detail.map.legend.facility'),
                connected: t('detail.map.legend.connected'),
                edge: t('detail.map.legend.edge'),
                cascadePill: t('detail.map.cascadePill'),
                levelPrefix: t('detail.map.levelPrefix'),
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
